import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { Id, Shortlist, ShortlistProfile } from "PFTypes";
import { useEffect, useState } from "react";

import { useShortlistCreate } from "./queries/shortlists/use_shortlist_create";
import { useShortlistDelete } from "./queries/shortlists/use_shortlist_delete";

type ShortlistMutationProps = { onSuccess?: () => void };

const useAddShortlist = (profileId: Id, activityId: Id, shortlists: Shortlist[] | null, named?: boolean) => {
  const growlErrors = useErrorsGrowl();
  const [shortlistItem, setShortlistItem] = useState<Partial<Shortlist> | null>(null);
  const { mutate: createShortlist, isPending: isCreating } = useShortlistCreate();
  const { mutate: deleteShortlist, isPending: isDeleting } = useShortlistDelete({ activityId });
  const isShortlistsLoaded = !!shortlists;

  useEffect(() => {
    if (shortlists) {
      setShortlistItem(
        shortlists.filter((shortlist) => shortlist.profile && shortlist.profile.id === profileId)[0]
      );
    }
  }, [shortlists]);

  const undoShortlist = ({ onSuccess }: ShortlistMutationProps = {}) => {
    const shortlistItemCopy = { ...shortlistItem };
    setShortlistItem(null);

    if (!shortlistItemCopy?.id) {
      return;
    }
    deleteShortlist(shortlistItemCopy.id, {
      onSuccess,
      onError: (response) => {
        setShortlistItem(shortlistItemCopy);
        growlErrors(response);
      }
    });
  };

  const shortlistProfile = ({ onSuccess }: ShortlistMutationProps = {}) => {
    setShortlistItem({
      activityId: Number(activityId),
      profile: { id: profileId } as ShortlistProfile,
      state: "pending"
    });

    createShortlist(
      { activityId, profileId: profileId, named },
      {
        onSuccess: (resp) => {
          setShortlistItem((prev) => ({ ...prev, id: resp.id }) as Shortlist);
          onSuccess?.();
        },
        onError: (response) => {
          // UNDO optimistic update
          setShortlistItem(null);
          growlErrors(response);
        }
      }
    );
  };

  const isShortlisted = !!shortlistItem;
  const loading = isCreating || isDeleting;

  return {
    isShortlisted,
    shortlistProfile,
    undoShortlist,
    loading,
    isShortlistsLoaded,
    shortlistItem
  };
};

export default useAddShortlist;
